import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Chip } from "../../components/ui/chip";
import { ScrollArea } from "../../components/ui/scroll-area";
import { ScheduleLine, UpfrontCharges, ScheduleConfig, Asset } from "../../types/schema";
import { Calendar, Clock, Repeat, TrendingUp, ChevronDown, ChevronUp, FileSpreadsheet } from "lucide-react";
import { formatDisplayDate } from "../../lib/dateFormat";
import { useEffect, useState } from "react";
import * as XLSX from 'xlsx';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  lines: ScheduleLine[];
  upfront: UpfrontCharges | null;
  config?: ScheduleConfig;
  assets?: Asset[];
  onConfirm?: () => void | Promise<void>;
  /** Show Back / Confirm & Create footer — only for create rental schedule flow */
  showConfirmActions?: boolean;
  /** Edit schedule preview — shows Back / Save footer */
  mode?: "create" | "edit";
}

export default function SchedulePreviewModal({
  open,
  onOpenChange,
  lines,
  upfront,
  config,
  assets,
  onConfirm,
  showConfirmActions = false,
  mode = "create",
}: Props) {
  const isEditMode = mode === "edit";
  const showFooter = showConfirmActions || isEditMode;
  const [showAllLines, setShowAllLines] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSubmitting(false);
      setShowAllLines(false);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (submitting || !onConfirm) return;
    setSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setSubmitting(false);
    }
  };
  
  const gstTotals = lines.reduce(
    (acc, l) => {
      acc.gst += l.gstRaw ?? 0;
      acc.base += l.baseRentRounded;
      acc.total += l.baseRentRounded + (l.gstRaw ?? 0);
      return acc;
    },
    {gst: 0, base: 0, total: 0 }
  );

  const downloadExcel = () => {
    const excelData = lines.map((line) => ({
      Period: `${line.periodNo}${line.isInterim ? ' (Interim)' : ''}`,
      'From Date': line.fromDate,
      'To Date': line.toDate,
      'Due Date': line.dueDate,
      'Base Rent': line.baseRentRounded,
      'GST Rate': `${line.gstRate}%`,
      'GST Amount': line.gstRaw,
      'Total Amount': line.baseRentRounded + (line.gstRaw ?? 0)
    }));

    // Add totals row
    excelData.push({
      Period: 'TOTALS',
      'From Date': '',
      'To Date': '',
      'Due Date': '',
      'Base Rent': gstTotals.base,
      'GST Rate': ``,
      'GST Amount': gstTotals.gst,
      'Total Amount': gstTotals.total
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Rental Schedule');
    XLSX.writeFile(workbook, 'rental-schedule.xlsx');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-4xl max-h-[90vh] flex flex-col gap-0 overflow-hidden p-0 sm:rounded-lg">
        <DialogHeader className="shrink-0 space-y-1.5 border-b border-border px-6 pb-4 pt-6 pr-12">
          <DialogTitle>Schedule Preview</DialogTitle>
          <DialogDescription>
            {isEditMode
              ? "Review schedule details before saving changes"
              : showConfirmActions
                ? "Review schedule details before confirmation"
                : "Rental schedule overview"}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="min-h-0 flex-1">
          <div className="space-y-4 px-6 py-5">
          {/* Schedule Summary Chips */}
          {config && (
            <div className="flex flex-wrap gap-2">
              <Chip variant="primary" className="flex items-center gap-1 text-xs">
                <Repeat className="h-3 w-3" />
                Frequency: {config.frequency.charAt(0).toUpperCase() + config.frequency.slice(1).toLowerCase()}
              </Chip>
              <Chip variant="info" className="flex items-center gap-1 text-xs">
                <TrendingUp className="h-3 w-3" />
                {(config.frequency === "monthly"  || config.frequency === "Monthly") ? `PTPM: ${config.ptpm?.toFixed(2) || 0}` : `PTPQ: ${config.ptpq?.toFixed(2) || config.ptpm?.toFixed(2) || 0}`}
              </Chip>
              <Chip variant="secondary" className="flex items-center gap-1 text-xs">
                <Clock className="h-3 w-3" />
                Tenure: {config.tenureMonths} months
              </Chip>
              <Chip variant="success" className="flex items-center gap-1 text-xs">
                <Calendar className="h-3 w-3" />
                Start: {formatDisplayDate(config.commencementDate)}
              </Chip>
              <Chip variant="warning" className="text-xs">
                Cycle Day: {config.paymentCycleDay}
              </Chip>
              <Chip variant="warning" className="text-xs">
                GST Rate: {config.gstRate}%
              </Chip>
            </div>
          )}

          {/* Dashboard Cards */}
          {upfront && assets && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="rounded-md border p-3">
                <div className="text-muted-foreground">Total Asset Value</div>
                <div className="font-medium">₹{Math.round(assets.reduce((sum, a) => sum + a.basePrice * (a.quantity || 1) * (1 + a.gstRate / 100), 0)).toLocaleString("en-IN")}</div>
                <div className="text-xs text-muted-foreground">Base + GST</div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-muted-foreground">Base Rental</div>
                <div className="font-medium">₹{lines[1]?.baseRentRounded.toLocaleString("en-IN")}</div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-muted-foreground">Upfront Total</div>
                <div className="font-medium">₹{upfront.totalRounded.toLocaleString("en-IN")}</div>
                <div className="text-xs text-muted-foreground">SD + PF</div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-muted-foreground">Total Receivables</div>
                <div className="font-medium">₹{gstTotals.total.toLocaleString("en-IN")}</div>
                <div className="text-xs text-muted-foreground">Total Base Rental</div>
              </div>
            </div>
          )}

          <div className="rounded-md border">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">Rental Schedule</h3>
              <div className="flex gap-2">
                <Button onClick={downloadExcel} variant="outline" size="sm" className="flex items-center gap-2">
                  <FileSpreadsheet className="h-4 w-4" />
                  Download Excel
                </Button>
              </div>
            </div>
            <div className="max-h-[380px] overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead>Due</TableHead>
                    <TableHead className="text-right">Base</TableHead>
                    <TableHead className="text-right">GST</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {showAllLines ? (
                    // Show all lines when expanded
                    lines.map((l) => (
                      <TableRow key={l.scheduleLineId}>
                        <TableCell>{l.periodNo}{l.isInterim ? " (Interim)" : ""}</TableCell>
                        <TableCell>{formatDisplayDate(l.fromDate)}</TableCell>
                        <TableCell>{formatDisplayDate(l.toDate)}</TableCell>
                        <TableCell>{formatDisplayDate(l.dueDate)}</TableCell>
                        <TableCell className="text-right">₹{l.baseRentRounded.toLocaleString("en-IN")}</TableCell>
                        <TableCell className="text-right">₹{l.gstRaw?.toLocaleString("en-IN")}</TableCell>
                        <TableCell className="text-right">₹{(l.baseRentRounded + (l.gstRaw ?? 0)).toLocaleString("en-IN")}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    // Show condensed view with expand button
                    <>
                      {lines.slice(0, 2).map((l) => (
                        <TableRow key={`${l.scheduleLineId}-head`}>
                          <TableCell>{l.periodNo}{l.isInterim ? " (Interim)" : ""}</TableCell>
                          <TableCell>{formatDisplayDate(l.fromDate)}</TableCell>
                          <TableCell>{formatDisplayDate(l.toDate)}</TableCell>
                          <TableCell>{formatDisplayDate(l.dueDate)}</TableCell>
                          <TableCell className="text-right">₹{l.baseRentRounded.toLocaleString("en-IN")}</TableCell>
                          <TableCell className="text-right">₹{l.gstRaw?.toLocaleString("en-IN")}</TableCell>
                          <TableCell className="text-right">₹{(l.baseRentRounded + (l.gstRaw ?? 0)).toLocaleString("en-IN")}</TableCell>
                        </TableRow>
                      ))}
                      {lines.length > 4 && (
                        <TableRow className="bg-muted/30 hover:bg-muted/50 cursor-pointer" onClick={() => setShowAllLines(true)}>
                          <TableCell colSpan={7} className="text-center text-muted-foreground py-4">
                            <div className="flex items-center justify-center gap-2">
                              <ChevronDown className="h-4 w-4" />
                              +{lines.length - 4} rentals (periods {lines[2]?.periodNo} to {lines[lines.length - 3]?.periodNo}) - Click to expand
                              <ChevronDown className="h-4 w-4" />
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                      {lines.length > 2 && lines.slice(-2).map((l) => (
                        <TableRow key={`${l.scheduleLineId}-tail`}>
                          <TableCell>{l.periodNo}{l.isInterim ? " (Interim)" : ""}</TableCell>
                          <TableCell>{formatDisplayDate(l.fromDate)}</TableCell>
                          <TableCell>{formatDisplayDate(l.toDate)}</TableCell>
                          <TableCell>{formatDisplayDate(l.dueDate)}</TableCell>
                          <TableCell className="text-right">₹{l.baseRentRounded.toLocaleString("en-IN")}</TableCell>
                            <TableCell className="text-right">₹{l.gstRaw?.toLocaleString("en-IN")}</TableCell>
                          {/* <TableCell className="text-right">₹{l.tdsRounded?.toLocaleString("en-IN") ?? 0}</TableCell> */}
                          <TableCell className="text-right">₹{(l.baseRentRounded + (l.gstRaw ?? 0)).toLocaleString("en-IN")}</TableCell>
                        </TableRow>
                      ))}
                    </>
                  )}
                  {showAllLines && lines.length > 4 && (
                    <TableRow className="bg-muted/30 hover:bg-muted/50 cursor-pointer" onClick={() => setShowAllLines(false)}>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-2">
                        <div className="flex items-center justify-center gap-2">
                          <ChevronUp className="h-4 w-4" />
                          Collapse view
                          <ChevronUp className="h-4 w-4" />
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell colSpan={4} className="text-right font-medium">Totals</TableCell>
                    <TableCell className="text-right font-medium">₹{gstTotals.base.toLocaleString("en-IN")}</TableCell>
                    <TableCell className="text-right font-medium">₹{gstTotals.gst.toLocaleString("en-IN")}</TableCell>
                    <TableCell className="text-right font-semibold">₹{gstTotals.total.toLocaleString("en-IN")}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Notes: Rounding to nearest INR. GST applied per asset HSN at 12/18/28%. Advance billing: due at start of period.
          </p>
          </div>
        </ScrollArea>

        {showFooter && (
          <DialogFooter className="shrink-0 gap-2 border-t border-border px-6 py-4 sm:space-x-0">
            <Button
              variant="outline"
              className="cursor-pointer"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Back
            </Button>
            <Button
              className="cursor-pointer"
              onClick={() => void handleConfirm()}
              disabled={submitting || !onConfirm}
            >
              {isEditMode
                ? submitting
                  ? "Saving..."
                  : "Save"
                : submitting
                  ? "Creating..."
                  : "Confirm & Create (Draft)"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
